import React, { useMemo } from 'react';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import MiniHourlyChart from './MiniHourlyChart';

// Bot data interface from API
export interface FDTreemapBotData {
  bot_name: string;
  client_name: string;
  total_hoje: number;
  total_ontem: number;
  total_semana: number;
  variation_vs_ontem: number;
  variation_vs_semana: number;
  // Anomaly detection fields
  expected_ontem: number;
  expected_semana: number;
  deficit_ontem: number;
  deficit_semana: number;
  anomaly_score: number;
  hourly: {
    hoje: number[];
    ontem: number[];
    semana_anterior: number[];
  };
}

// Treemap node interface
export interface TreemapNode {
  name: string;
  size: number;
  value: number;
  variation: number;
  fill: string;
  botId: string;
  botData: FDTreemapBotData;
}

interface FDTreemapChartProps {
  data: TreemapNode[];
  loading?: boolean;
  onBotClick?: (botId: string, botData: FDTreemapBotData) => void;
}

/**
 * Get color based on variation percentage
 */
const getColorForVariation = (variation: number): string => {
  if (typeof variation !== 'number' || isNaN(variation)) return '#374151';
  if (variation >= 50) return '#15803d';
  if (variation >= 20) return '#22c55e';
  if (variation >= 5) return '#4ade80';
  if (variation > -5) return '#374151';
  if (variation > -20) return '#f87171';
  if (variation > -50) return '#ef4444';
  return '#b91c1c';
};

/**
 * Custom content renderer for treemap cells
 * Recharts Treemap passes all node properties directly to content component
 */
const CustomizedContent: React.FC<any> = (props) => {
  const {
    x, y, width, height,
    name, botData, variation, botId,
    depth, onBotClick
  } = props;

  if (depth === 0) return null;

  const safeVariation = typeof variation === 'number' && !isNaN(variation) ? variation : 0;
  const safeName = name || 'Bot';
  const safeWidth = Math.max(width || 0, 0);
  const safeHeight = Math.max(height || 0, 0);
  const safeX = x || 0;
  const safeY = y || 0;

  if (safeWidth <= 0 || safeHeight <= 0) return null;

  const baseColor = getColorForVariation(safeVariation);
  const radius = Math.min(4, safeWidth / 10, safeHeight / 10);

  const handleClick = () => {
    if (onBotClick && botId && botData) {
      onBotClick(botId, botData);
    }
  };

  // Very small cells
  if (safeWidth < 40 || safeHeight < 30) {
    return (
      <g onClick={handleClick} style={{ cursor: 'pointer' }}>
        <rect
          x={safeX + 1}
          y={safeY + 1}
          width={safeWidth - 2}
          height={safeHeight - 2}
          fill={baseColor}
          rx={radius}
          ry={radius}
        />
      </g>
    );
  }

  const padding = 6;
  const showChart = safeWidth >= 120 && safeHeight >= 90 && botData?.hourly;
  const chartHeight = Math.min(safeHeight - 50, 45);
  const chartWidth = safeWidth - padding * 2;

  const variationStr = safeVariation > 0 ? `+${safeVariation.toFixed(0)}%` : `${safeVariation.toFixed(0)}%`;
  const variationColor = safeVariation >= 5 ? '#86efac' : safeVariation <= -5 ? '#fca5a5' : '#9ca3af';

  const maxNameLength = Math.floor((safeWidth - 50) / 6);
  const displayName = safeName.length > maxNameLength && maxNameLength > 3
    ? safeName.substring(0, maxNameLength - 2) + '..'
    : safeName;

  // Dynamic font sizes based on cell size
  const nameFontSize = safeWidth > 150 ? 11 : safeWidth > 100 ? 10 : 9;
  const varFontSize = safeWidth > 150 ? 10 : 9;
  const countFontSize = safeWidth > 120 ? 9 : 8;

  return (
    <g style={{ transition: 'opacity 0.2s', cursor: 'pointer' }} onClick={handleClick}>
      {/* Background with rounded corners and subtle inner glow */}
      <rect
        x={safeX + 1}
        y={safeY + 1}
        width={safeWidth - 2}
        height={safeHeight - 2}
        fill={baseColor}
        rx={radius}
        ry={radius}
      />
      {/* Top highlight for depth effect */}
      <rect
        x={safeX + 2}
        y={safeY + 2}
        width={safeWidth - 4}
        height={Math.min(20, safeHeight / 3)}
        fill="url(#cellHighlight)"
        rx={radius}
        ry={radius}
        style={{ pointerEvents: 'none' }}
      />

      {safeWidth > 50 && (
        <text
          x={safeX + padding}
          y={safeY + 14}
          fill="#fff"
          fontSize={nameFontSize}
          fontWeight="600"
          style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
        >
          {displayName}
        </text>
      )}

      {safeWidth > 70 && (
        <text
          x={safeX + safeWidth - padding}
          y={safeY + 14}
          fill={variationColor}
          fontSize={varFontSize}
          fontWeight="700"
          textAnchor="end"
        >
          {variationStr}
        </text>
      )}

      {showChart && (
        <foreignObject
          x={safeX + padding}
          y={safeY + 22}
          width={chartWidth}
          height={chartHeight}
        >
          <MiniHourlyChart
            hourly={botData.hourly}
            width={chartWidth}
            height={chartHeight}
          />
        </foreignObject>
      )}

      {safeWidth > 60 && safeHeight > 55 && (
        <text
          x={safeX + padding}
          y={safeY + safeHeight - 6}
          fill="rgba(255,255,255,0.7)"
          fontSize={countFontSize}
        >
          {(botData?.total_hoje || 0).toLocaleString()} int
        </text>
      )}
    </g>
  );
};

/**
 * Custom tooltip component
 */
const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  const botData: FDTreemapBotData = data.botData;
  if (!botData) return null;

  const varOntem = botData.variation_vs_ontem || 0;
  const varSemana = botData.variation_vs_semana || 0;
  const expectedOntem = botData.expected_ontem || 0;
  const expectedSemana = botData.expected_semana || 0;
  const deficitOntem = botData.deficit_ontem || 0;
  const deficitSemana = botData.deficit_semana || 0;
  const anomalyScore = botData.anomaly_score || 0;

  // Determine anomaly status
  const isAnomaly = anomalyScore > 30;
  const isCritical = anomalyScore > 60;

  return (
    <div style={{
      backgroundColor: 'rgba(17, 24, 39, 0.98)',
      border: isCritical ? '1px solid rgba(239, 68, 68, 0.6)' : isAnomaly ? '1px solid rgba(251, 191, 36, 0.5)' : '1px solid rgba(75, 85, 99, 0.5)',
      borderRadius: '12px',
      padding: '14px',
      minWidth: '280px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      backdropFilter: 'blur(8px)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid rgba(75,85,99,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>
              {botData.bot_name || 'Bot'}
            </div>
            <div style={{ color: '#6b7280', fontSize: '11px' }}>
              {botData.client_name || ''}
            </div>
          </div>
          {isAnomaly && (
            <div style={{
              backgroundColor: isCritical ? 'rgba(239, 68, 68, 0.2)' : 'rgba(251, 191, 36, 0.2)',
              color: isCritical ? '#fca5a5' : '#fcd34d',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              {isCritical ? 'CRITICO' : 'ANOMALIA'}
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#60a5fa', fontSize: '18px', fontWeight: '700', lineHeight: '1.2' }}>
            {(botData.total_hoje || 0).toLocaleString()}
          </div>
          <div style={{ color: '#6b7280', fontSize: '10px', marginTop: '2px' }}>Hoje</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#a78bfa', fontSize: '18px', fontWeight: '700', lineHeight: '1.2' }}>
            {(botData.total_ontem || 0).toLocaleString()}
          </div>
          <div style={{ color: '#6b7280', fontSize: '10px', marginTop: '2px' }}>Ontem</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#34d399', fontSize: '18px', fontWeight: '700', lineHeight: '1.2' }}>
            {(botData.total_semana || 0).toLocaleString()}
          </div>
          <div style={{ color: '#6b7280', fontSize: '10px', marginTop: '2px' }}>Semana</div>
        </div>
      </div>

      {/* Expected vs Actual - New Section */}
      <div style={{
        backgroundColor: 'rgba(75,85,99,0.15)',
        borderRadius: '8px',
        padding: '10px',
        marginBottom: '12px'
      }}>
        <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '8px', fontWeight: '500' }}>
          ESPERADO ATE AGORA
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ color: '#a78bfa', fontSize: '14px', fontWeight: '600' }}>
              {expectedOntem.toLocaleString()}
            </div>
            <div style={{ color: '#6b7280', fontSize: '9px' }}>base ontem</div>
            {deficitOntem > 0 && (
              <div style={{ color: '#fca5a5', fontSize: '10px', fontWeight: '600' }}>
                -{deficitOntem.toLocaleString()}
              </div>
            )}
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ color: '#34d399', fontSize: '14px', fontWeight: '600' }}>
              {expectedSemana.toLocaleString()}
            </div>
            <div style={{ color: '#6b7280', fontSize: '9px' }}>base semana</div>
            {deficitSemana > 0 && (
              <div style={{ color: '#fca5a5', fontSize: '10px', fontWeight: '600' }}>
                -{deficitSemana.toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Variation Bars */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <div style={{ flex: 1, backgroundColor: 'rgba(75,85,99,0.2)', borderRadius: '6px', padding: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#9ca3af', fontSize: '10px' }}>vs Ontem</span>
            <span style={{
              color: varOntem >= 0 ? '#86efac' : '#fca5a5',
              fontWeight: '700',
              fontSize: '12px'
            }}>
              {varOntem > 0 ? '+' : ''}{varOntem.toFixed(1)}%
            </span>
          </div>
        </div>
        <div style={{ flex: 1, backgroundColor: 'rgba(75,85,99,0.2)', borderRadius: '6px', padding: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#9ca3af', fontSize: '10px' }}>vs Semana</span>
            <span style={{
              color: varSemana >= 0 ? '#86efac' : '#fca5a5',
              fontWeight: '700',
              fontSize: '12px'
            }}>
              {varSemana > 0 ? '+' : ''}{varSemana.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Mini Chart */}
      {botData.hourly && (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px' }}>
          <MiniHourlyChart
            hourly={botData.hourly}
            width={256}
            height={55}
            showLegend={true}
          />
        </div>
      )}
    </div>
  );
};

/**
 * FD Treemap Chart Component
 */
const FDTreemapChart: React.FC<FDTreemapChartProps> = ({ data, loading, onBotClick }) => {
  // Process data for treemap
  const treemapData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Filter out items with zero or negative size and ensure valid data
    const validData = data
      .filter(node => node && node.size > 0 && node.name)
      .map(node => ({
        ...node,
        // Ensure all required fields have values
        name: node.name || 'Bot',
        size: Math.max(node.size, 1),
        variation: typeof node.variation === 'number' ? node.variation : 0,
        fill: getColorForVariation(node.variation),
        // Pass the click handler to each node
        onBotClick
      }));

    return validData;
  }, [data, onBotClick]);

  if (loading) {
    return (
      <div style={{
        height: '600px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#111827',
        borderRadius: '8px'
      }}>
        <div style={{ color: '#9ca3af' }}>Carregando mapa de calor...</div>
      </div>
    );
  }

  if (!treemapData || treemapData.length === 0) {
    return (
      <div style={{
        height: '600px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#111827',
        borderRadius: '8px'
      }}>
        <div style={{ color: '#9ca3af' }}>Nenhum dado disponivel</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '650px' }}>
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="cellHighlight" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>
      </svg>
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={treemapData}
          dataKey="size"
          aspectRatio={16 / 9}
          stroke="none"
          fill="#374151"
          content={<CustomizedContent />}
        >
          <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 100 }} />
        </Treemap>
      </ResponsiveContainer>
    </div>
  );
};

export default FDTreemapChart;
